import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  communicationTabApi,
  CommunicationTabFilters,
  CreateCommunicationPayload,
  UpdateCommunicationPayload,
} from "../apis/communicationTab";
import {
  Communication,
  CommunicationPayload,
} from "../types/communicationTypes";
import { PaginatedResponse } from "@core/types";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import { toast } from "@shared/ui/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
export interface UseCommunicationReturn {
  // Data
  communications: Communication[];
  isLoading: boolean;
  isError: boolean;
  error: any;

  // Mutations
  createCommunication: (payload: CreateCommunicationPayload) => Promise<void>;
  isCreating: boolean;
}

export function useCommunications(
  filters: CommunicationTabFilters,
  options?: {
    enabled?: boolean;
  }
): UseCommunicationReturn {
  const queryClient = useQueryClient();

  // Create communication mutation
  const createMutation = useMutation({
    mutationFn: communicationTabApi.createCommunication,
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Communication created successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({ queryKey: ["communications"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create communication",
        variant: "destructive",
      });
    },
  });

  // Helper functions
  const createCommunication = useCallback(
    async (payload: CreateCommunicationPayload) => {
      await createMutation.mutateAsync(payload);
    },
    [createMutation]
  );

  return {
    // Data
    communications: [],
    isLoading: false,
    isError: false,
    error: null,

    // Mutations
    createCommunication,
    // Loading states
    isCreating: createMutation.isPending,
  };
}

// Hook for communications by consumer
export function useCommunicationHistory(params: {
  consumerId: string;
  remoteUtilityId: number;
  actionType?: string;
}) {
  const { data: communications, isLoading } = useQuery({
    queryKey: ["communications", params],
    queryFn: () =>
      communicationTabApi.getCommunicationHistory(params.consumerId, {
        page: DEFAULT_PAGE_NUMBER,
        limit: PAGE_SIZE,
        remoteUtilityId: params.remoteUtilityId,
        communicationType: params.actionType
          ? params.actionType
          : "consumer_support_request",
        entityId: Number(params.consumerId),
      }),
    enabled: !!params.consumerId && !!params.remoteUtilityId,
  });

  return {
    communications:
      (communications as PaginatedResponse<Communication>)?.results || [],
    isLoading,
  };
}

// Hook for communication types
export function useCommunicationTypes() {
  const { data: types, isLoading } = useQuery({
    queryKey: ["communicationTypes"],
    queryFn: () => communicationTabApi.getCommunicationTypes(),
  });

  return {
    types: (types as { result: any[] })?.result || [],
    isLoading,
  };
}

// Hook for channel types
export function useChannelTypes() {
  const { data: types, isLoading } = useQuery({
    queryKey: ["channelTypes"],
    queryFn: () => communicationTabApi.getChannelTypes(),
  });

  return {
    types: (types as { result: any[] })?.result || [],
    isLoading,
  };
}

// Hook for templates by channel type
export function useTemplatesByChannelType(params: {
  channelType: string;
  remoteUtilityId: number;
}) {
  const { data: templates, isLoading } = useQuery({
    queryKey: ["templatesByChannelType", params],
    queryFn: () => communicationTabApi.getTemplatesByChannelType(params),
    enabled: !!params.channelType && !!params.remoteUtilityId,
  });

  return {
    templates: (templates as { result: any[] })?.result || [],
    isLoading,
  };
}

// Hook for communication summary
export function useCommunicationSummary(params: {
  remoteUtilityId: number;
  entityId?: number;
  requestId?: string;
  consumerId?: string;
}) {
  const { data: summary, isLoading } = useQuery({
    queryKey: ["communicationSummary", params],
    queryFn: () => communicationTabApi.getCommunicationSummary(params),
    enabled: !!params.remoteUtilityId,
  });

  return {
    summary: (summary as { result: any })?.result,
    isLoading,
  };
}

// Hook for call purpose
export const useCallPurpose = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.communication.callPurpose(),
    () => communicationTabApi.callPurpose(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

// Hook for call status
export const useCallStatus = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.communication.callStatus(),
    () => communicationTabApi.callStatus(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useCreateCallEntry = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (data: any) => communicationTabApi.createCallEntry(data),
    {
      onSuccess: () => {
        toast({
          title: "Call Entry created successfully!",
        });
                queryClient.invalidateQueries({
          queryKey: ["callEntries"],
          exact: false,
        });
      },
    }
  );
};

export const useGetCallEntryById = (id: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  
  return useSmartQuery(
    QueryKeyFactory.module.cx.communication.callEntry(id),
    () => communicationTabApi.getCallEntryById(id,remoteUtilityId),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetCommunicationHistory = (
  remote_utility_id: string,
  consumer_id: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.communication.communicationHistory(consumer_id),
    () =>
      communicationTabApi.getAllCommunicationHistory(
        remote_utility_id,
        consumer_id
      ),
    {
      enabled: !!remote_utility_id && !!consumer_id,
    }
  );
};
